import {
  AfterViewInit,
  Component,
  computed,
  inject,
  OnChanges,
  output,
  SimpleChanges
} from '@angular/core';
import { TruncateParagraphPipe } from '../../../pipes/truncate-paragraph.pipe';
import { ChatMessageService } from '../services/chat-message.service';
import { CommonModule } from '@angular/common';
import { ChatRole } from '../../../enums/api.enum';
import { ThemeService } from '../../../services/theme.service';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MermaidDirective } from '../directives/mermaid.directive';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Clipboard } from '@angular/cdk/clipboard';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { RequestDetailComponent } from './request-detail/request-detail.component';
import { IMessage } from '../../../interfaces/i-chat-session';
import { AttachmentDocComponent } from '../../../shared/attachment-doc/attachment-doc.component';
import { ChatSessionService } from "../services/chat-session.service";
import { ActionType } from "../../../types/app.types";
import { SessionManagerComponent } from "../session-manager/session-manager.component";
import { DynamicMarkdownComponent } from './dynamic-markdown/dynamic-markdown.component';
import { DynamicSnackBarService } from '../../../shared/dynamic-snack-bar/dynamic-snack-bar.service';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { QuickAddPromptDialogComponent } from './quick-add-prompt-dialog/quick-add-prompt-dialog.component';
import { AutomationStateService } from "../services/automation-state.service";
import { MatExpansionModule } from '@angular/material/expansion';

/** Minimum word count before a bubble gets the collapse/expand toggle. */
const COLLAPSE_WORD_THRESHOLD = 40;

/** Number of words shown in the collapsed preview summary line. */
const COLLAPSE_PREVIEW_WORD_COUNT = 30;

@Component({
  selector: 'chat-message-view',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatProgressBarModule,
    MermaidDirective,
    AttachmentDocComponent,
    DynamicMarkdownComponent,
    TruncateParagraphPipe
  ],
  templateUrl: './message-view.component.html',
  styleUrl: './message-view.component.scss'
})
export class MessageViewComponent implements OnChanges, AfterViewInit {
  messageAdded = output<void>();

  /** Exposed to the template so the truncation pipe argument stays in sync with the collapsibility threshold. */
  readonly collapsePreviewWordCount = COLLAPSE_PREVIEW_WORD_COUNT;

  chatRole = ChatRole;
  chatMessageService = inject(ChatMessageService);
  chatSessionService = inject(ChatSessionService);
  themeService = inject(ThemeService);
  clipboard = inject(Clipboard); // Inject Clipboard service
  automationStateService = inject(AutomationStateService);
  #snackBarService = inject(DynamicSnackBarService);

  readonly dialog = inject(MatDialog);

  /**
   * Transforms a flat message array into conversation turn groups.
   *
   * Pairs each user message with its corresponding AI/system responses, enabling a
   * structured conversation layout where each turn is rendered as a single container
   * with the user query on the right and all AI responses below.
   *
   * @returns Array of conversation groups, each containing a user message and zero or more AI responses.
   *
   * @example
   * // Input (flat array):
   * [
   *   { id: '1', role: 'user', formatted: 'What is AI?' },
   *   { id: '2', role: 'assistant', formatted: 'AI is...' },
   *   { id: '3', role: 'user', formatted: 'Tell me more' },
   *   { id: '4', role: 'assistant', formatted: 'More details...' }
   * ]
   *
   * // Output (grouped):
   * [
   *   {
   *     userMessage: { id: '1', role: 'user', formatted: 'What is AI?' },
   *     aiMessages: [
   *       { id: '2', role: 'assistant', formatted: 'AI is...' }
   *     ]
   *   },
   *   {
   *     userMessage: { id: '3', role: 'user', formatted: 'Tell me more' },
   *     aiMessages: [
   *       { id: '4', role: 'assistant', formatted: 'More details...' }
   *     ]
   *   }
   * ]
   *
   * // Usage in template:
   * // @for (group of groupedMessages(); track group.userMessage.id) {
   * //   <div class="conversation-container">
   * //     <div class="user-query">{{ group.userMessage.formatted }}</div>
   * //     @for (aiMsg of group.aiMessages; track aiMsg.id) {
   * //       <div class="ai-response">{{ aiMsg.formatted }}</div>
   * //     }
   * //   </div>
   * // }
   */
  readonly groupedMessages = computed(() => {
    const messages = this.chatMessageService.messages() ?? [];
    const groups: Array<{
      userMessage: IMessage;
      aiMessages: IMessage[];
    }> = [];

    let currentUserMessage: IMessage | null = null;
    let currentAiMessages: IMessage[] = [];

    for (const message of messages) {
      if (message.role === ChatRole.User) {
        // If we have a pending user message group, save it
        if (currentUserMessage) {
          groups.push({
            userMessage: currentUserMessage,
            aiMessages: currentAiMessages,
          });
        }
        // Start a new user message group
        currentUserMessage = message;
        currentAiMessages = [];
      } else {
        // This is an AI or system message - add to current group
        if (currentUserMessage) {
          currentAiMessages.push(message);
        }
      }
    }

    // Don't forget the last group if it exists
    if (currentUserMessage && currentAiMessages.length > 0) {
      groups.push({
        userMessage: currentUserMessage,
        aiMessages: currentAiMessages,
      });
    }

    // Handle the case where we have a user message but no AI response yet
    if (currentUserMessage && currentAiMessages.length === 0) {
      groups.push({
        userMessage: currentUserMessage,
        aiMessages: currentAiMessages,
      });
    }

    return groups;
  });

  constructor() {
    }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['chatMessageService']) {
      this.notifyMessageAdded();
    }
  }

  ngAfterViewInit(): void {
    this.notifyMessageAdded();
  }

  /**
   * Generates a mermaid.js diagram.
   */
  generateMermaidDiagram(workflow: string[]): string {
    const diagram = ['graph LR'];
    workflow.forEach((step, index) => {
      let nodeShape;
      const sanitizedStep = step.replace(/ /g, '_'); // Replace spaces with underscores
      /*
      This switch is here to decide which node shape to display.  Right now, we are only going to use the default and AI options.
      We need to investigate on if it's possible to do this.  We may need to implement another field for agents such as a type.
      It seems that whatever is displayed is the agent's name.
      */
      switch (step) {
        case 'AI':
          nodeShape = `([${sanitizedStep}])`; // Stadium-shaped node
          break;
        case 'DB':
          nodeShape = `[(${sanitizedStep})]`; // Cylindrical shape
          break;
        default:
          nodeShape = `(${sanitizedStep})`; // Node with round edges
        // nodeShape = `[${step}]`; // Default to square node
      }
      diagram.push(`${sanitizedStep}${nodeShape}`);
      if (index < workflow.length - 1) {
        const nextStep = workflow[index + 1].replace(/ /g, '_');
        diagram.push(`${sanitizedStep} --> ${nextStep}`);
      }
    });
    return diagram.join('\n');
  }

  showRequestDetails(message: IMessage) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = message;
    dialogConfig.minWidth = '50%';
    dialogConfig.autoFocus = 'dialog';

    this.dialog.open(RequestDetailComponent, dialogConfig);
  }

  /**
   * Notifies the application that a new message has rendered.
   */
  notifyMessageAdded() {
    this.messageAdded.emit();
  }


  /**
   * Copy the message to the clipboard.
   */
  copyToClipboard(content: string) {
    this.clipboard.copy(content);
    this.#snackBarService.openSnackBar('Copied message!');
  }

  /**
   * Quick add a prompt to the Automation Library.
   */
  quickAddPromptToPromptLibrary(content: string) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.data = content;
    dialogConfig.minWidth = '50%';
    dialogConfig.autoFocus = 'dialog';

    this.dialog.open(QuickAddPromptDialogComponent, dialogConfig);
  }

  /**
   * Returns true if the message content is long enough to warrant a collapsible bubble.
   * Uses the same word-count threshold as TruncateParagraphPipe.
   */
  isCollapsible(text: string | undefined): boolean {
    if (!text) return false;
    const words = this.stripPseudoXml(text).split(/\s+/).filter(w => /[a-zA-Z0-9]/.test(w));
    return words.length > COLLAPSE_WORD_THRESHOLD;
  }

  /** Strips all pseudo-XML blocks (<thinking>, <tool_use>, <tool_result>) from text before preview/collapsibility checks. */
  stripPseudoXml(text: string): string {
    return text
      .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
      .replace(/<tool_use>[\s\S]*?<\/tool_use>/gi, '')
      .replace(/<tool_result>[\s\S]*?<\/tool_result>/gi, '')
      .trim();
  }

  /**
   * Checks if a property exists on an object.
   *
   * NOTE: This method had to be created because Angular cannot interpret Object.hasOwn(obj, property) in an @if () {...} template.
   */
  hasProperty(obj: any, property: string): boolean {
    if (!obj) {
      return false;
    }
    return Object.hasOwn(obj, property);
  }

  openCreateSessionDialog(action: ActionType) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = false;
    dialogConfig.width = '100vw';
    dialogConfig.height = '100vh';
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '100vh';
    dialogConfig.data = { action: action };

    const dialogRef = this.dialog.open(SessionManagerComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(id => {
      if (id) {
        this.chatSessionService.completeCreateOrEditSession(id, action);
      }
    });
  }
}
